// translate router.meta.title, be used in breadcrumb sidebar tagsview
import i18n from '@/lang/index';
import cache from '@/plugins/cache';

export const TENANT_I18N_CONFIG_KEY = 'Tenant-I18n-Config';

/** 币种配置（/setting/i18n/config 返回的 currency） */
export interface CurrencyConfigVo {
  /** 币种代码，如 CNY */
  currency: string;
  /** 金额精度（小数位数），如 2 */
  moneyPrecision: number;
}

/** 语言配置（/setting/i18n/config 返回的 locale） */
export interface LocaleConfigVo {
  /** 默认语言，如 zh-CN */
  defaultLocale: string;
  /** 地区代码，如 CN */
  region: string;
  /** 支持的语言列表 */
  supportedLocales: string[];
}

/** 时间配置（/setting/i18n/config 返回的 time） */
export interface TimeConfigVo {
  /** 日期格式，如 yyyy-MM-dd */
  dateFormat: string;
  /** 日期时间格式，如 yyyy-MM-dd HH:mm:ss */
  dateTimeFormat: string;
  /** 每周第一天，如 MONDAY */
  firstDayOfWeek: string;
  /** IANA 时区标识，如 Asia/Shanghai */
  timeZone: string;
}

/** 当前租户前端国际化配置（/setting/i18n/config 响应 data） */
export interface TenantI18nConfig {
  /** 币种配置 */
  currency?: CurrencyConfigVo;
  /** 语言配置 */
  locale?: LocaleConfigVo;
  /** 配置版本号 */
  schemaVersion?: number;
  /** 时间配置 */
  time?: TimeConfigVo;
}

/**
 * 缓存当前租户前端国际化配置
 */
export const setTenantI18nConfigCache = (config: TenantI18nConfig) => {
  cache.local.setJSON(TENANT_I18N_CONFIG_KEY, config || {});
};

/**
 * 获取当前租户前端国际化配置缓存
 */
export const getTenantI18nConfigCache = (): TenantI18nConfig => {
  return cache.local.getJSON(TENANT_I18N_CONFIG_KEY) || {};
};

/**
 * 获取当前租户币种配置（/setting/i18n/config 的 currency 缺省回退 CNY / 2 位小数）
 */
export const getTenantCurrencyConfig = (): CurrencyConfigVo => {
  const config = getTenantI18nConfigCache();
  return {
    currency: config.currency?.currency || 'CNY',
    moneyPrecision: config.currency?.moneyPrecision ?? 2,
  };
};

/** 常见币种展示文案（公共参数，key 为界面语言；未收录的币种回退币种代码本身） */
const CURRENCY_DISPLAY: Record<string, Record<string, string>> = {
  CNY: { zh_CN: 'CNY（人民币）', en_US: 'CNY (RMB)' },
  USD: { zh_CN: 'USD（美元）', en_US: 'USD (US Dollar)' },
  HKD: { zh_CN: 'HKD（港币）', en_US: 'HKD (Hong Kong Dollar)' },
  JPY: { zh_CN: 'JPY（日元）', en_US: 'JPY (Japanese Yen)' },
  EUR: { zh_CN: 'EUR（欧元）', en_US: 'EUR (Euro)' },
  GBP: { zh_CN: 'GBP（英镑）', en_US: 'GBP (British Pound)' },
};

/**
 * 获取租户币种展示文案（如 CNY（人民币）/ CNY (RMB)）
 * @param locale 界面语言（zh_CN / en_US），缺省取当前语言
 */
export const getTenantCurrencyDisplay = (locale?: string): string => {
  const { currency } = getTenantCurrencyConfig();
  const currentLocale = locale || i18n.global.locale.value;
  return CURRENCY_DISPLAY[currency]?.[currentLocale] || `${currency}（${currency}）`;
};

/**
 * 清除当前租户前端国际化配置缓存
 */
export const removeTenantI18nConfigCache = () => {
  cache.local.remove(TENANT_I18N_CONFIG_KEY);
};

/**
 * 获取国际化路由，如果不存在则原生返回
 * @param title 路由名称
 * @returns {string}
 */
export const translateRouteTitle = (title: string): string => {
  const hasKey = i18n.global.te(`route.${ title}`);
  if (hasKey) {
    const translatedTitle = i18n.global.t(`route.${ title}`);
    return translatedTitle;
  }
  return title;
};
